import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../lib/api";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";

type TerminalEntry = {
  id: string;
  command: string;
  stdout: string;
  stderr: string;
  exitCode: number | null;
  status: "running" | "done" | "error";
};

export function TerminalWorkbench() {
  const [command, setCommand] = useState("");
  const [entries, setEntries] = useState<TerminalEntry[]>([
    {
      id: "boot",
      command: "system ready",
      stdout: "OpenTool desktop terminal connected to /api/exec\nWorking directory: workspace root",
      stderr: "",
      exitCode: 0,
      status: "done",
    },
  ]);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [entries]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function runCommand() {
    const trimmed = command.trim();
    if (!trimmed || running) return;

    const entryId = `cmd-${Date.now()}`;
    setRunning(true);
    setError(null);
    setHistory((prev) => (prev[prev.length - 1] === trimmed ? prev : [...prev, trimmed]));
    setHistoryIndex(null);
    setEntries((prev) => [
      ...prev,
      {
        id: entryId,
        command: trimmed,
        stdout: "",
        stderr: "",
        exitCode: null,
        status: "running",
      },
    ]);
    setCommand("");

    try {
      const res = await apiFetch("/api/exec", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command: trimmed }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        const message = data.error || `request failed (${res.status})`;
        setEntries((prev) =>
          prev.map((entry) =>
            entry.id === entryId
              ? { ...entry, stderr: message, status: "error", exitCode: null }
              : entry,
          ),
        );
        setError(message);
        return;
      }

      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                stdout: data.stdout || "",
                stderr: data.stderr || "",
                exitCode: typeof data.exit_code === "number" ? data.exit_code : null,
                status: "done",
              }
            : entry,
        ),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "network error";
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === entryId
            ? { ...entry, stderr: message, status: "error", exitCode: null }
            : entry,
        ),
      );
      setError(message);
    } finally {
      setRunning(false);
      inputRef.current?.focus();
    }
  }

  function recallHistory(direction: "up" | "down") {
    if (!history.length) return;
    if (direction === "up") {
      const nextIndex = historyIndex === null ? history.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(nextIndex);
      setCommand(history[nextIndex]);
      return;
    }

    if (historyIndex === null) return;
    const nextIndex = historyIndex + 1;
    if (nextIndex >= history.length) {
      setHistoryIndex(null);
      setCommand("");
      return;
    }
    setHistoryIndex(nextIndex);
    setCommand(history[nextIndex]);
  }

  return (
    <div className="ot-desktop-terminal">
      <CodexTerminalFrame title="Console" prompt="desktop shell" className="ot-desktop-terminal__frame">
        <div className="ot-desktop-terminal__panel">
          <div className="ot-desktop-terminal__toolbar">
            <div>
              <strong>workspace shell</strong>
              <p>Governed command execution over the OpenTool control plane.</p>
            </div>
            <div className="ot-desktop-terminal__meta">
              <span>{running ? "running" : "idle"}</span>
              <span>{entries.length - 1} commands</span>
            </div>
          </div>

          <div className="ot-desktop-terminal__screen" ref={scrollRef}>
            {entries.map((entry) => (
              <article key={entry.id} className={`ot-desktop-terminal__entry is-${entry.status}`}>
                <header className="ot-desktop-terminal__entry-head">
                  <span className="ot-desktop-terminal__sigil">$</span>
                  <code>{entry.command}</code>
                  <span className="ot-desktop-terminal__entry-status">
                    {entry.status === "running"
                      ? "running"
                      : entry.exitCode === null
                        ? entry.status
                        : `exit ${entry.exitCode}`}
                  </span>
                </header>
                {entry.stdout ? <pre className="ot-desktop-terminal__stream is-stdout">{entry.stdout}</pre> : null}
                {entry.stderr ? <pre className="ot-desktop-terminal__stream is-stderr">{entry.stderr}</pre> : null}
              </article>
            ))}
          </div>

          <div className="ot-desktop-terminal__composer">
            <span className="ot-desktop-terminal__sigil">$</span>
            <input
              ref={inputRef}
              value={command}
              onChange={(event) => setCommand(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void runCommand();
                  return;
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  recallHistory("up");
                  return;
                }
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  recallHistory("down");
                }
              }}
              placeholder="run a command in the workspace root"
              spellCheck={false}
            />
            <button type="button" onClick={() => void runCommand()} disabled={running || !command.trim()}>
              {running ? "..." : "run"}
            </button>
          </div>

          <div className="ot-desktop-terminal__footer">
            <span>history: up/down</span>
            <span>cwd: workspace root</span>
            {error ? <span className="is-error">last error: {error}</span> : <span>transport: /api/exec</span>}
          </div>
        </div>
      </CodexTerminalFrame>
    </div>
  );
}
